import PropTypes from 'prop-types';
import { PageBlock } from '@etg/wings';
import { useProperty, useSiteContext, useTranslation } from '@eti/providers';
import {
  AddOnOrder,
  BoundNew,
  FareRule,
  FlightInformation,
  PriceFormat,
  RegionNew,
  SegmentNew,
  TripCharacteristics,
} from '@eti/types';
import { constants } from '@eti/utils';
import { Helmet } from 'react-helmet';
import DebugProviderSystemBar from '../../../widgets/trip-details/components/DebugProviderSystemBar';
import { shouldShowDebugProviderSystemBar } from '../../../utils/debug';
import { useEcomPurchaseDataLayerEffect } from '../../../common/data-layer/ecommerce/custom-hooks/useEcomPurchaseDataLayerEffect';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../utils/carriersUtils';
import { isFutureTravelDate } from '../utils/utils';
import { isTravellingViaRome } from '../utils/adr';
import Banners from '../../../common/components/Banners';
import CrossSellBanner from './CrossSellBanner';
import ReceiptDetails from './ReceiptDetails';
import ExtraProducts from './ReceiptExtraProducts';
import ConfirmationMessage from './ReceiptConfirmationMessage';
import Flights from './ReceiptFlights';
import TripDetails from './ReceiptTripDetails';
import UpsellWidgets from './UpsellWidgets';

export const getTravelerPricesTotal = (travelerPrices = []) =>
  travelerPrices.reduce((total, { totalPriceSum }) => total + totalPriceSum, 0);

const shouldShowAddress = (customerDetails) =>
  customerDetails ? Boolean(customerDetails.street) : false;

const { brands } = constants;

const Receipt = ({
  addOnOrders,
  baggageInfoUrl,
  bounds,
  bundles,
  cancellationMessage,
  cc2carrierBreakdown,
  chargeCurrencyPriceFormat,
  checkedBaggageSummary,
  customerDetails,
  destination,
  discountPrice,
  shouldDisplayNotification,
  fareRules,
  flightInformation,
  instalmentsTotalFee,
  isMultiStop,
  isOtherChargeCurrency,
  isPostBookingOrder,
  isUpsellAllowed,
  language = {},
  obfuscatedOrderReference,
  oldOrderNumber,
  orderExtraProducts,
  orderNumber,
  orderReferenceSerialized,
  paymentProducts,
  paymentTypes,
  priceInChargeCurrency,
  postBookingUrl,
  purchaseDate,
  segments,
  texts = {},
  totalPrice,
  totalPriceIncludingAddOnOrders,
  travelDocUrl,
  travelers,
  travelerPrices,
  tripProviderSystems,
  tripCharacteristics = [],
}) => {
  useEcomPurchaseDataLayerEffect(
    bundles,
    discountPrice,
    isPostBookingOrder,
    obfuscatedOrderReference,
    orderExtraProducts,
    paymentProducts,
    travelerPrices,
  );

  const { t } = useTranslation();
  const { p } = useProperty();
  const { brand } = useSiteContext();
  const shouldShowOrderInformation = p('IbeClient.OrderInformation.Enabled');
  const hasRecentPostBookingOrder = Boolean(isPostBookingOrder && addOnOrders?.length);
  const showExtraProducts = isFutureTravelDate(segments);
  const shouldShowBrandedBanner = p('Order.ShowBrandedBanner.Enabled');
  const shouldShowCrossSellBanner = isTravellingViaRome(segments) && brand?.code === brands.ADR;
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );
  const isRyanairFlight = isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent;

  return (
    <>
      <Helmet title={t('PageTitle.rf.order.action')} />
      <section data-testid="order-page">
        {shouldShowDebugProviderSystemBar(tripProviderSystems) && (
          <DebugProviderSystemBar providerSystems={tripProviderSystems} />
        )}
        <div
          data-orderid={parseInt(oldOrderNumber, 36)}
          data-testid="edvin-order-link-placeholder"
        />

        <Flights
          destination={destination}
          segments={segments}
          shouldDisplayNotification={shouldDisplayNotification}
        />
        <ConfirmationMessage
          cancellationMessage={cancellationMessage}
          customerDetails={customerDetails}
          fareRules={fareRules}
          hasRecentPostBookingOrder={hasRecentPostBookingOrder}
          languageCode={language.code}
          orderNumber={orderNumber}
          purchaseDate={purchaseDate}
          shouldShowOrderInformation={shouldShowOrderInformation}
          texts={texts}
          travelDocUrl={travelDocUrl}
        />
        {showExtraProducts && <ExtraProducts postBookingUrl={postBookingUrl} />}
        {shouldShowBrandedBanner && (
          <PageBlock withPaddingY>
            <Banners />
          </PageBlock>
        )}
        {shouldShowCrossSellBanner && <CrossSellBanner />}
        {isUpsellAllowed && !shouldShowBrandedBanner && (
          <UpsellWidgets
            bounds={bounds}
            locale={language.code}
            nationalityId={customerDetails.nationality.code}
            obfuscatedOrderNumber={orderNumber}
            purchaseDate={purchaseDate}
            travelers={travelers}
          />
        )}
        <TripDetails
          baggageInfoUrl={baggageInfoUrl}
          bounds={bounds}
          checkedBaggageSummary={checkedBaggageSummary}
          flightInformation={flightInformation}
          isMultiStop={isMultiStop}
          segments={segments}
          travelers={travelers}
          tripCharacteristics={tripCharacteristics}
        />
        <ReceiptDetails
          addOnOrders={addOnOrders}
          bounds={bounds}
          bundles={bundles}
          cc2carrierBreakdown={cc2carrierBreakdown}
          chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
          customerDetails={customerDetails}
          discountPrice={discountPrice}
          instalmentsTotalFee={instalmentsTotalFee}
          isOtherChargeCurrency={isOtherChargeCurrency}
          isRyanairFlight={isRyanairFlight}
          orderExtraProducts={orderExtraProducts}
          orderNumber={orderNumber}
          orderReferenceSerialized={orderReferenceSerialized}
          orderedAt={purchaseDate}
          paymentTypes={paymentTypes}
          priceInChargeCurrency={priceInChargeCurrency}
          shouldShowAddress={shouldShowAddress(customerDetails)}
          totalPrice={totalPrice}
          totalPriceIncludingAddOnOrders={totalPriceIncludingAddOnOrders}
          travelerPrices={travelerPrices}
        />
      </section>
    </>
  );
};

const baggageDetails = PropTypes.shape({
  __typename: PropTypes.string,
  baggageDetails: PropTypes.shape({
    __typename: PropTypes.string,
    pieces: PropTypes.any,
    weight: PropTypes.number,
    weightUnit: PropTypes.string,
  }),
  baggageStatus: PropTypes.string,
  travelerId: PropTypes.string,
});

Receipt.propTypes = {
  addOnOrders: PropTypes.arrayOf(AddOnOrder),
  baggageInfoUrl: PropTypes.string,
  bounds: PropTypes.arrayOf(BoundNew),
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.string),
      title: PropTypes.string,
    }),
  ),
  cancellationMessage: PropTypes.shape({
    header: PropTypes.string,
    text: PropTypes.string,
  }),
  cc2carrierBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      carrierName: PropTypes.string,
      transactionAmount: PropTypes.shape({
        price: PropTypes.shape({
          currency: PropTypes.shape({
            exponent: PropTypes.number,
            name: PropTypes.string,
          }),
          value: PropTypes.number,
        }),
      }),
    }),
  ),
  chargeCurrencyPriceFormat: PriceFormat,
  checkedBaggageSummary: PropTypes.shape({
    __typename: PropTypes.string,
    boundSummaries: PropTypes.arrayOf(
      PropTypes.shape({
        __typename: PropTypes.string,
        boundId: PropTypes.string,
        segmentSummaries: PropTypes.arrayOf(
          PropTypes.shape({
            __typename: PropTypes.string,
            segmentId: PropTypes.string,
            summaries: PropTypes.arrayOf(baggageDetails),
          }),
        ),
        summaries: PropTypes.arrayOf(baggageDetails),
      }),
    ),
    tripSummaries: PropTypes.arrayOf(baggageDetails),
  }),
  customerDetails: PropTypes.shape({
    city: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    nationality: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
    phone: PropTypes.string,
    street: PropTypes.string,
    zipCode: PropTypes.string,
  }),
  destination: RegionNew,
  discountPrice: PropTypes.number,
  fareRules: PropTypes.arrayOf(FareRule),
  flightInformation: PropTypes.arrayOf(FlightInformation),
  instalmentsTotalFee: PropTypes.number,
  isMultiStop: PropTypes.bool,
  isOtherChargeCurrency: PropTypes.bool,
  isPostBookingOrder: PropTypes.bool,
  isUpsellAllowed: PropTypes.bool,
  language: PropTypes.shape({
    code: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  obfuscatedOrderReference: PropTypes.string,
  oldOrderNumber: PropTypes.string,
  orderExtraProducts: PropTypes.arrayOf(PropTypes.object),
  orderNumber: PropTypes.string,
  orderReferenceSerialized: PropTypes.string,
  paymentProducts: PropTypes.arrayOf(PropTypes.object),
  paymentTypes: PropTypes.arrayOf(PropTypes.object),
  postBookingUrl: PropTypes.string,
  priceInChargeCurrency: PropTypes.number,
  purchaseDate: PropTypes.string,
  segments: PropTypes.arrayOf(SegmentNew),
  shouldDisplayNotification: PropTypes.bool,
  texts: PropTypes.shape({
    carWidget: PropTypes.string,
    confirmationAndWelcomeContent: PropTypes.string,
    confirmationAndWelcomeTitle: PropTypes.string,
    confirmationMessage: PropTypes.string,
    hotelWidget: PropTypes.string,
    importantInformation: PropTypes.string,
    systemLCCTitleAndContent: PropTypes.string,
  }),
  totalPrice: PropTypes.number,
  totalPriceIncludingAddOnOrders: PropTypes.number,
  travelDocUrl: PropTypes.string,
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
  travelers: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      birthDate: PropTypes.string,
      firstName: PropTypes.string,
      gender: PropTypes.string,
      id: PropTypes.string,
      lastName: PropTypes.string,
      name: PropTypes.string,
      passportNumber: PropTypes.string,
      products: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
          quantity: PropTypes.number,
        }),
      ),
    }),
  ),
  tripCharacteristics: PropTypes.arrayOf(TripCharacteristics),
  tripProviderSystems: PropTypes.arrayOf(PropTypes.string),
};

export default Receipt;
